import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

type UnsubscribeStatus = "loading" | "success" | "error" | "already_unsubscribed";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<UnsubscribeStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  
  const email = searchParams.get("email");
  const newsletterType = searchParams.get("type") || "general";

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!email) {
        setStatus("error");
        setErrorMessage("No email address provided. Please use the unsubscribe link from your email.");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("handle-unsubscribe", {
          body: { 
            email: decodeURIComponent(email),
            newsletterType 
          },
        });

        if (error) {
          console.error("Unsubscribe error:", error);
          setStatus("error");
          setErrorMessage("Something went wrong. Please try again or contact support.");
          return;
        }

        if (data?.alreadyUnsubscribed) {
          setStatus("already_unsubscribed");
        } else if (data?.success) {
          setStatus("success");
        } else {
          setStatus("error");
          setErrorMessage(data?.message || "Unable to process your request.");
        }
      } catch (err) {
        console.error("Unsubscribe exception:", err);
        setStatus("error");
        setErrorMessage("A network error occurred. Please try again.");
      }
    };

    handleUnsubscribe();
  }, [email, newsletterType]);

  return (
    <>
      <SEO
        title="Unsubscribe"
        description="Manage your SeniorThrive email preferences."
        noindex
      />

      <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center">
          {status === "loading" && (
            <div className="space-y-4">
              <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-foreground">
                Processing your request...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we update your preferences.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">
                You've been unsubscribed
              </h1>
              <p className="text-muted-foreground">
                We're sorry to see you go! You will no longer receive emails from us at{" "}
                <span className="font-medium text-foreground">{email}</span>.
              </p>
              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Changed your mind? You can always resubscribe.
                </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild>
                    <Link to="/">Return Home</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {status === "already_unsubscribed" && (
            <div className="space-y-6">
              <CheckCircle className="h-16 w-16 text-blue-500 mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">
                Already unsubscribed
              </h1>
              <p className="text-muted-foreground">
                This email address is already unsubscribed from our mailing list.
              </p>
              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Want to receive our updates again?
                </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild>
                    <Link to="/">Return Home</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-6">
              <XCircle className="h-16 w-16 text-destructive mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                {errorMessage}
              </p>
              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Need help? Contact us at{" "}
                  <a 
                    href="mailto:support@seniorthrive.com" 
                    className="text-primary underline hover:no-underline"
                  >
                    support@seniorthrive.com
                  </a>
                </p>
                <Button asChild>
                  <Link to="/">Return Home</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Unsubscribe;
